import React, { useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import PDFResultPage from './PDFResultPage'
import { analyzePDF } from './pdfUtils'
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useAuth } from '../../hooks/useSupabaseAuth.jsx'
import { useSubscription } from '../../hooks/useSubscription'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { AGENT_TYPES } from '../../constants/agentConfig.js'

// PDF Agent States
const PDF_STATES = {
  ANALYZING: 'analyzing',
  COMPLETED: 'completed'
}

const PDFAgentContent = ({ query, onBack, llmService, options }) => {
  // Debug logging for options
  console.log('🔍 PDFAgent received options:', options)
  console.log('🔍 PDFAgent pdfFile from options:', options?.pdfFile)
  
  const { user } = useAuth()
  const { consumeCredits } = useSubscription()
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const [currentState, setCurrentState] = useState(PDF_STATES.ANALYZING)
  const [analysisResult, setAnalysisResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pdfFile, setPdfFile] = useState(options?.pdfFile || null)
  const hasInitialized = useRef(false)
  const isAnalyzing = useRef(false)
  const hasCompletedAnalysis = useRef(false) // Track if analysis has ever completed successfully
  
  // Debug logging for pdfFile state
  console.log('🔍 PDFAgent pdfFile state:', pdfFile)
  
  // Check if we already have a result for this query in sessionStorage
  React.useEffect(() => {
    // If no PDF file is provided, show error immediately
    if (!pdfFile) {
      setError('File PDF tidak ditemukan. Silakan upload file PDF terlebih dahulu.')
      setIsLoading(false)
      return
    }
    
    const fileKey = pdfFile ? pdfFile.name || 'pdf' : 'no-pdf'
    const sessionKey = `pdf_result_${query}_${fileKey}`
    const cachedResult = sessionStorage.getItem(sessionKey)
    
    if (cachedResult) {
      try {
        const parsedResult = JSON.parse(cachedResult)
        console.log('📋 Loading cached PDF result for query:', query)
        setAnalysisResult(parsedResult)
        setCurrentState(PDF_STATES.COMPLETED)
        hasCompletedAnalysis.current = true
        setIsLoading(false)
        return
      } catch (error) {
        console.warn('⚠️ Failed to parse cached result, will analyze PDF again')
        sessionStorage.removeItem(sessionKey)
      }
    }
    
    // Only analyze if we don't have cached result and haven't initialized yet
    if (query && pdfFile && !hasInitialized.current && !isAnalyzing.current) {
      hasInitialized.current = true
      handleStartAnalysis()
    }
  }, [query, pdfFile]) // Include query and pdfFile in dependencies

  const handleStartAnalysis = useCallback(async () => {
    // Prevent multiple simultaneous analyses
    if (isAnalyzing.current) {
      console.log('⚠️ Analysis already in progress, skipping...')
      return
    }
    
    // Additional check: if we already have a completed analysis, don't analyze again
    if (currentState === PDF_STATES.COMPLETED && analysisResult) {
      console.log('⚠️ PDF already analyzed and completed, skipping...')
      return
    }
    
    // Strong protection: if analysis has ever completed successfully, don't analyze again
    if (hasCompletedAnalysis.current) {
      console.log('⚠️ Analysis has already completed successfully before, skipping...')
      return
    }
    
    if (!pdfFile) {
      setError('File PDF tidak ditemukan. Silakan upload file PDF terlebih dahulu.')
      setIsLoading(false)
      return
    }
    
    isAnalyzing.current = true
    setCurrentState(PDF_STATES.ANALYZING)
    setIsLoading(true)
    setError(null)
    setAnalysisResult(null)
    
    try {
      // Check credits before starting analysis with timeout and fallback
      let canProceed = true
      try {
        const creditCheckPromise = checkAndWarnCredits(AGENT_TYPES.PDF)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Credit check timeout')), 5000)
        )
        
        canProceed = await Promise.race([creditCheckPromise, timeoutPromise])
      } catch (creditError) {
        console.warn('⚠️ Credit check failed, proceeding with analysis:', creditError.message)
        // Continue with analysis even if credit check fails
        canProceed = true
      }
      
      if (!canProceed) {
        setIsLoading(false)
        return // Stop if insufficient credits
      }

      console.log(`🔄 Memulai analisis PDF untuk: ${query}`)
      
      const result = await analyzePDF(pdfFile, query, llmService)
      
      setAnalysisResult(result)
      
      // Cache the result in sessionStorage to prevent re-analysis on page revisit
      const fileKey = pdfFile ? pdfFile.name || 'pdf' : 'no-pdf'
      const sessionKey = `pdf_result_${query}_${fileKey}`
      try {
        sessionStorage.setItem(sessionKey, JSON.stringify(result))
        console.log('💾 PDF result cached for future visits')
      } catch (cacheError) {
        console.warn('⚠️ Failed to cache result:', cacheError)
      }
      
      console.log('🎉 PDF berhasil dianalisis')
      setCurrentState(PDF_STATES.COMPLETED)
      hasCompletedAnalysis.current = true // Mark as completed to prevent future analyses
      
      // Consume credits after successful PDF analysis
      try {
        await consumeCredits(AGENT_TYPES.PDF, {
          query: query,
          agentType: AGENT_TYPES.PDF,
          agentName: 'PDF Agent',
          fileName: pdfFile.name
        })
        console.log('✅ Credits consumed for PDF Agent')
      } catch (creditError) {
        console.error('❌ Failed to consume credits for PDF Agent:', creditError)
      }
      
      // Save to agent history
      try {
        await AgentHistoryIntegration.savePDFResult({
          query: query,
          fileName: pdfFile.name,
          fileSize: pdfFile.size,
          analysis: result.analysis, // Only save the analysis string, not the entire object
          extractedTextLength: result.extractedTextLength || 0,
          mode: result.mode || 'AI Model'
        })
        console.log('✅ Hasil analisis PDF berhasil disimpan ke history')
      } catch (historyError) {
        console.error('❌ Error menyimpan ke history:', historyError)
      }
    } catch (err) {
      console.error('❌ Error dalam handleStartAnalysis:', err)
      
      // Clear any cached result for this query since analysis failed
      const fileKey = pdfFile ? pdfFile.name || 'pdf' : 'no-pdf'
      const sessionKey = `pdf_result_${query}_${fileKey}`
      try {
        sessionStorage.removeItem(sessionKey)
        console.log('🗑️ Cleared cached result due to analysis error')
      } catch (cacheError) {
        console.warn('⚠️ Failed to clear cache after error:', cacheError)
      }
      
      // Provide user-friendly error messages
      let userMessage = err.message
      if (err.message.includes('API Key')) {
        userMessage = 'Konfigurasi API bermasalah. Silakan hubungi administrator.'
      } else if (err.message.includes('timeout')) {
        userMessage = 'Koneksi timeout. Silakan coba lagi dengan koneksi internet yang stabil.'
      } else if (err.message.includes('NetworkError') || err.message.includes('fetch')) {
        userMessage = 'Masalah koneksi internet. Periksa koneksi Anda dan coba lagi.'
      } else if (err.message.includes('PDF')) {
        userMessage = 'Gagal membaca file PDF. Pastikan file tidak rusak atau terenkripsi.'
      } else if (!err.message || err.message.trim() === '') {
        userMessage = 'Terjadi kesalahan tidak dikenal. Silakan coba lagi.'
      }
      
      setError(userMessage)
      setCurrentState(PDF_STATES.ANALYZING) // Keep in analyzing state to allow retry
      hasInitialized.current = false // Reset initialization flag on error to allow retry
    } finally {
      setIsLoading(false)
      isAnalyzing.current = false // Reset flag when analysis completes
    }
  }, [query, pdfFile, llmService]) // Include dependencies

  const handleReanalyze = useCallback(() => {
    // Clear cached results
    const fileKey = pdfFile ? pdfFile.name || 'pdf' : 'no-pdf'
    const sessionKey = `pdf_result_${query}_${fileKey}`
    
    try {
      sessionStorage.removeItem(sessionKey)
      console.log('🗑️ Cleared cached results for re-analysis')
    } catch (error) {
      console.warn('⚠️ Failed to clear cache:', error)
    }
    
    // Reset all analysis flags for re-analysis
    isAnalyzing.current = false
    hasCompletedAnalysis.current = false // Allow re-analysis
    hasInitialized.current = false // Reset initialization flag
    
    // Force re-analysis by resetting state first
    setCurrentState(PDF_STATES.ANALYZING)
    setAnalysisResult(null)
    setError(null)
    
    console.log('🔄 Manual re-analysis triggered')
    
    // Call analysis
    handleStartAnalysis()
  }, [query, pdfFile, handleStartAnalysis])

  // Always show result page (no outline page needed)
  return (
    <>
      <PDFResultPage
        query={query}
        analysisResult={analysisResult}
        isLoading={isLoading}
        error={error}
        isCompleted={currentState === PDF_STATES.COMPLETED}
        pdfFile={pdfFile}
        onBack={onBack}
        onReanalyze={handleReanalyze}
      />
      
      {/* Credit Warning Modal */}
      <CreditWarningModal
        isOpen={warningModal.isOpen}
        onClose={closeWarning}
        actionType={warningModal.actionType}
        creditsRequired={warningModal.creditsRequired}
        creditsRemaining={warningModal.creditsRemaining}
        packageName={warningModal.packageName}
        isFreeUser={warningModal.isFreeUser}
        message={warningModal.message}
      />
    </>
  )
}

// Main PDFAgent component
const PDFAgent = (props) => {
  return <PDFAgentContent {...props} />
}

PDFAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object,
  options: PropTypes.object
}

PDFAgentContent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object,
  options: PropTypes.object
}

export default PDFAgent